import { renderRuleFormula, ruleReferencesUnresolvedColumn } from "@/lib/schemaBuilderHelpers";
import { parseFile } from "@/services/fileParser";
import type { SchemaRule, SchemaRulePreset } from "@/types/schemaBuilder";
import * as Select from "@radix-ui/react-select";
import { ChevronDown, Upload, X } from "lucide-react";
import { useRef, useState } from "react";

interface RuleConfigurationModalProps {
  column?: {
    fieldId: string;
    id: string;
    name: string;
    dataType: string;
    example: string;
  };
  preset: SchemaRulePreset;
  existingRule?: SchemaRule;
  isEditing?: boolean;
  referenceListOptions?: { id: string; name: string }[];
  onCreateReferenceList?: (name: string, values: string[]) => string;
  onFileUploaded?: (fileName: string) => void;
  dateColumnOptions?: { name: string; fieldId: string }[];
  columnOptions?: { name: string; fieldId: string }[];
  allColumnOptions?: { name: string; fieldId: string }[];
  numericColumnOptions?: { name: string; fieldId: string }[];
  columnsByName?: Record<string, string>;
  existingRules?: Pick<SchemaRule, "id" | "ruleKey">[];
  onBack: () => void;
  onClose: () => void;
  onSave: (rule: SchemaRule) => void;
}

function normalizeRuleName(name: string): string {
  return name.trim().toLowerCase();
}

export function RuleConfigurationModal({
  column,
  preset,
  existingRule,
  isEditing = false,
  referenceListOptions = [],
  onCreateReferenceList,
  onFileUploaded,
  dateColumnOptions = [],
  columnOptions = [],
  allColumnOptions = [],
  numericColumnOptions = [],
  columnsByName = {},
  existingRules = [],
  onBack,
  onClose,
  onSave,
}: RuleConfigurationModalProps) {
  const isInReferenceList = preset.backendRuleType === "in_reference_list";
  const isNotInFuture = preset.backendRuleType === "not_in_future";
  const isNotInPast = preset.backendRuleType === "not_in_past";
  const isMustBeInPast = preset.backendRuleType === "must_be_in_past";
  const isBeforeOtherDate = preset.backendRuleType === "before_other_date";
  const existingParams = existingRule?.parameters ?? {};

  const [ruleKey, setRuleKey] = useState(() => {
    if (isEditing && existingRule?.ruleKey) return existingRule.ruleKey;
    if (column?.name && preset.backendRuleType === "mandatory") return `${column.name} - mandatory`;
    if (column?.name && preset.backendRuleType === "column_unique")
      return `${column.name} - unique`;
    return generateRuleKey(column?.name ?? "", preset.title);
  });
  const [description, setDescription] = useState(
    isEditing ? (existingRule?.description ?? "") : ""
  );
  const criticality = (preset.defaultCriticality ?? existingRule?.criticality ?? "error") as
    | "error"
    | "warn";

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const schemaPropsForInit =
    (preset.parametersSchema?.properties as Record<string, unknown> | undefined) ?? {};
  const brt = preset.backendRuleType ?? "";
  const isIfThenRule = brt === "if_x_positive_y_required" || brt === "if_x_negative_y_required";
  const currentColIsNumeric = column?.dataType === "Integer" || column?.dataType === "Decimal";
  const hasAnchor = !isEditing && !!column;
  let primaryParamKey: string | undefined;
  if (hasAnchor) {
    if (brt === "subtract_equals") primaryParamKey = "x_column";
    else if (brt === "sum_n_equals_total")
      primaryParamKey = "total_column" in schemaPropsForInit ? "total_column" : "col";
    else if (isIfThenRule) primaryParamKey = currentColIsNumeric ? "x_column" : "y_column";
  }
  const lockEarlierColumn = hasAnchor;

  const [paramValues, setParamValues] = useState<Record<string, unknown>>(() => {
    const schemaProps =
      (preset.parametersSchema?.properties as Record<string, unknown> | undefined) ?? {};
    const toleranceProp = schemaProps.tolerance as Record<string, unknown> | undefined;
    const toleranceDefault =
      toleranceProp && !("tolerance" in existingParams)
        ? { tolerance: (toleranceProp.default as number) ?? 0 }
        : {};
    const primaryColDefault =
      primaryParamKey && !(primaryParamKey in existingParams)
        ? { [primaryParamKey]: column?.name ?? "" }
        : {};
    const refListDefault =
      isInReferenceList && !("reference_list_id" in existingParams)
        ? { reference_list_id: "" }
        : {};
    const laterColDefault =
      isBeforeOtherDate && !("later_column" in existingParams) ? { later_column: "" } : {};
    const earlierColDefault =
      isBeforeOtherDate && !lockEarlierColumn && !("earlier_column" in existingParams)
        ? { earlier_column: "" }
        : {};
    return {
      ...toleranceDefault,
      ...primaryColDefault,
      ...refListDefault,
      ...earlierColDefault,
      ...laterColDefault,
      ...existingParams,
    };
  });

  // ── Reference list picker state (select existing / add new) ──

  const referenceListNameById = Object.fromEntries(
    referenceListOptions.map((opt) => [opt.id, opt.name])
  );

  const [isAddingList, setIsAddingList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListValues, setNewListValues] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editedListValues = newListValues
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

  function handleSelectListChange(value: string) {
    if (value === "add-new") {
      setNewListName("");
      setNewListValues("");
      setIsAddingList(true);
      return;
    }
    handleParamChange("reference_list_id", value);
    setIsAddingList(false);
  }

  function handleCreateListDone() {
    const listName = newListName.trim();
    if (!listName || !onCreateReferenceList) return;

    const values = editedListValues;
    const newId = onCreateReferenceList(listName, values);
    handleParamChange("reference_list_id", newId);
    setIsAddingList(false);
    setNewListName("");
    setNewListValues("");
  }

  function handleCreateListCancel() {
    setIsAddingList(false);
    setNewListName("");
    setNewListValues("");
  }

  async function handleListUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = "";

    try {
      const sheets = await parseFile(file);
      const sheet = sheets[0];
      if (!sheet) return;

      // parseFile treats row 0 as a header — use it as the list name, and every
      // subsequent row as a value.
      const values = sheet.rows
        .map((row) => String(Object.values(row)[0] ?? ""))
        .map((v) => v.trim())
        .filter(Boolean);

      setNewListName(sheet.headers[0]?.trim() || file.name.replace(/\.[^/.]+$/, ""));
      setNewListValues(values.join(", "));
      onFileUploaded?.(file.name);
    } catch {
      // leave existing values intact if parsing fails
    }
  }

  // ── Parameters builder ───────────────────────────────────────

  function buildParameters(): Record<string, unknown> {
    if (isInReferenceList) {
      return { reference_list_id: paramValues.reference_list_id, case_sensitive: false };
    }
    if (isNotInFuture || isNotInPast || isMustBeInPast) {
      return {};
    }
    if (isBeforeOtherDate) {
      return {
        earlier_column: lockEarlierColumn ? (column?.name ?? "") : paramValues.earlier_column,
        later_column: paramValues.later_column,
      };
    }
    return paramValues;
  }

  function handleParamChange(key: string, value: unknown) {
    setParamValues((prev) => ({ ...prev, [key]: value }));
  }

  const schemaProperties =
    (preset.parametersSchema?.properties as Record<string, Record<string, unknown>> | undefined) ??
    {};
  const explicitRequired = (preset.parametersSchema?.required as string[] | undefined) ?? [];
  // If the backend omits a `required` array, treat all non-boolean properties as required
  const requiredParamKeys =
    explicitRequired.length > 0
      ? explicitRequired
      : Object.entries(schemaProperties)
          .filter(([, prop]) => prop.type !== "boolean")
          .map(([key]) => key);
  const allRequiredFilled = requiredParamKeys.every((k) => {
    const val = paramValues[k];
    if (Array.isArray(val)) return val.length > 0;
    return val !== undefined && val !== null && val !== "";
  });
  const minVal = paramValues.min as number | undefined;
  const maxVal = paramValues.max as number | undefined;
  const minMaxValid = minVal === undefined || maxVal === undefined || minVal <= maxVal;

  const earlierColumnVal = (paramValues.earlier_column as string) ?? "";
  const isEarlierColumnOrphaned =
    !lockEarlierColumn &&
    earlierColumnVal.length > 0 &&
    !dateColumnOptions.some((opt) => opt.name === earlierColumnVal);

  const referenceListIdVal = (paramValues.reference_list_id as string) ?? "";
  const isReferenceListOrphaned =
    referenceListIdVal.length > 0 &&
    !referenceListOptions.some((opt) => opt.id === referenceListIdVal);

  const isDuplicateName =
    ruleKey.trim().length > 0 &&
    existingRules.some(
      (r) =>
        r.id !== existingRule?.id && normalizeRuleName(r.ruleKey) === normalizeRuleName(ruleKey)
    );

  const canSave =
    !isDuplicateName &&
    (isBeforeOtherDate
      ? !!paramValues.later_column && (lockEarlierColumn || !!paramValues.earlier_column)
      : isInReferenceList
        ? !!paramValues.reference_list_id && !isAddingList
        : allRequiredFilled && minMaxValid);

  function handleSave() {
    const params = buildParameters();

    // include the anchor column, if any, alongside columns resolved from the parameters
    const referencedFieldIds = Object.values(params)
      .flatMap((v) => (Array.isArray(v) ? v : [v]))
      .filter((v): v is string => typeof v === "string")
      .map((name) => columnsByName[name])
      .filter((id): id is string => !!id);
    const columnIds = [...new Set([...(column ? [column.fieldId] : []), ...referencedFieldIds])];

    const hasUnresolvedColumn = ruleReferencesUnresolvedColumn(
      { parameters: params },
      columnsByName
    );

    const rule: SchemaRule = {
      id: isEditing ? (existingRule?.id ?? crypto.randomUUID()) : crypto.randomUUID(),
      title: preset.title,
      category: preset.category,
      fieldCount: columnIds.length,
      ruleKey,
      columnIds,
      formula: renderRuleFormula(preset, paramValues, column?.name ?? "", referenceListNameById),
      description: description,
      hasConflict: hasUnresolvedColumn,
      backendRuleType: preset.backendRuleType,
      dqType: preset.dqType,
      presetId: preset.id,
      parameters: buildParameters(),
      criticality,
      scope: preset.scope ?? "column",
    };
    onSave(rule);
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40">
      <div className="flex max-h-[85vh] w-[720px] flex-col rounded-lg bg-white p-5 shadow-2xl">
        <div className="mb-4 flex shrink-0 items-start justify-between">
          <div>
            <div className="flex items-center gap-8">
              <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                {preset.title}
              </h2>

              {column ? (
                <>
                  <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                    {column.name}
                  </span>

                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {column.dataType}
                  </span>

                  <span className="text-sm text-[var(--color-text-tertiary)]">
                    Ex. {column.example || "---"}
                  </span>
                </>
              ) : (
                <span className="text-sm text-[var(--color-text-secondary)]">
                  Cross-column rule
                </span>
              )}
            </div>

            <p className="mt-3 text-sm text-[var(--color-text-primary)]">{preset.description}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid flex-1 grid-cols-2 gap-8 overflow-y-auto pr-1">
          {/* ── Left column: rule-specific configuration ─────── */}
          <div className="space-y-4">
            {isInReferenceList && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-semibold">Values in</label>
                  <input
                    value={column?.name ?? ""}
                    readOnly
                    className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold">
                    Must be a value in
                    <span className="ml-0.5 text-red-500">*</span>
                  </label>

                  <Select.Root
                    value={isReferenceListOrphaned ? "" : referenceListIdVal}
                    onValueChange={handleSelectListChange}
                  >
                    <Select.Trigger
                      className={`flex h-10 w-full items-center justify-between rounded border bg-white px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-action-primary)] ${
                        isReferenceListOrphaned ? "border-red-400" : "border-[var(--color-border)]"
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate text-left">
                        <Select.Value placeholder="Select a reference list" />
                      </span>
                      <Select.Icon>
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-tertiary)]" />
                      </Select.Icon>
                    </Select.Trigger>

                    <Select.Portal>
                      <Select.Content
                        position="popper"
                        sideOffset={4}
                        className="z-[90] max-h-[240px] w-[260px] overflow-y-auto rounded-md border border-[var(--color-border)] bg-white py-1 shadow-lg"
                      >
                        <Select.Viewport>
                          {referenceListOptions.map((opt) => (
                            <Select.Item
                              key={opt.id}
                              value={opt.id}
                              className="cursor-pointer px-3 py-2 text-xs text-[var(--color-text-primary)] outline-none data-[highlighted]:bg-[var(--color-bg-secondary)]"
                            >
                              <Select.ItemText>{opt.name}</Select.ItemText>
                            </Select.Item>
                          ))}

                          <Select.Item
                            value="add-new"
                            className="cursor-pointer px-3 py-2 text-xs font-medium text-blue-600 outline-none data-[highlighted]:bg-[var(--color-bg-secondary)]"
                          >
                            <Select.ItemText>Add New</Select.ItemText>
                          </Select.Item>
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                  {isReferenceListOrphaned && (
                    <p className="mt-1 text-xs text-red-500">
                      This reference list no longer exists. Select a replacement to resolve the
                      conflict.
                    </p>
                  )}
                </div>

                {isAddingList && (
                  <div className="space-y-3 rounded border border-[var(--color-border)] p-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx"
                      className="hidden"
                      onChange={handleListUpload}
                    />

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs font-semibold">List Name</label>
                        <input
                          value={newListName}
                          onChange={(event) => setNewListName(event.target.value)}
                          placeholder="Enter List Name"
                          className="h-9 w-full rounded border border-[var(--color-border)] px-2 text-xs"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold">Upload List</label>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex h-9 w-full items-center justify-center rounded border border-[var(--color-border)] text-[var(--color-action-primary)]"
                        >
                          <Upload className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold">
                        Manual Entry
                        <span className="ml-1 font-normal text-[var(--color-text-tertiary)]">
                          (Case-sensitive, separate values by comma)
                        </span>
                      </label>
                      <textarea
                        value={newListValues}
                        onChange={(event) => setNewListValues(event.target.value)}
                        placeholder="Input list items here"
                        className="h-20 w-full resize-none rounded border border-[var(--color-border)] px-3 py-2 text-xs outline-none focus:border-[var(--color-action-primary)]"
                      />

                      <div className="mt-2 flex justify-end gap-3 text-xs">
                        <button
                          type="button"
                          onClick={handleCreateListCancel}
                          className="text-red-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleCreateListDone}
                          className="text-blue-600"
                        >
                          Done
                        </button>
                      </div>
                    </div>

                    {editedListValues.length > 0 && (
                      <div className="max-h-32 overflow-y-auto">
                        <div className="flex flex-wrap gap-2">
                          {editedListValues.map((value) => (
                            <span
                              key={value}
                              className="rounded border border-green-300 bg-green-50 px-2 py-1 text-xs text-green-700"
                            >
                              {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {(isNotInFuture || isNotInPast || isMustBeInPast) && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-semibold">Values in</label>
                  <input
                    value={column?.name ?? ""}
                    readOnly
                    className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold">
                      {isNotInFuture
                        ? "Occur on or before"
                        : isNotInPast
                          ? "Occur on or after"
                          : "Must be before"}
                    </label>
                    <input
                      value="Present Day"
                      readOnly
                      className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm text-[var(--color-text-secondary)]"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold">Time Zone</label>
                    <input
                      value={userTimezone}
                      readOnly
                      className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm text-[var(--color-text-secondary)]"
                    />
                  </div>
                </div>
              </>
            )}

            {isBeforeOtherDate && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-semibold">
                    Earlier date column
                    {!lockEarlierColumn && <span className="ml-0.5 text-red-500">*</span>}
                  </label>
                  {lockEarlierColumn ? (
                    <input
                      value={column?.name ?? ""}
                      readOnly
                      className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm"
                    />
                  ) : (
                    <>
                      <select
                        value={isEarlierColumnOrphaned ? "" : earlierColumnVal}
                        onChange={(e) => handleParamChange("earlier_column", e.target.value)}
                        className={`h-10 w-full rounded border px-3 text-sm outline-none focus:border-[var(--color-action-primary)] ${
                          isEarlierColumnOrphaned
                            ? "border-red-400"
                            : "border-[var(--color-border)]"
                        }`}
                      >
                        <option value="">Select column</option>
                        {dateColumnOptions.map((opt) => (
                          <option key={opt.fieldId} value={opt.name}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                      {isEarlierColumnOrphaned && (
                        <p className="mt-1 text-xs text-red-500">
                          Column &quot;{earlierColumnVal}&quot; no longer exists. Select a
                          replacement to resolve the conflict.
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold">
                    Later date column
                    <span className="ml-0.5 text-red-500">*</span>
                  </label>
                  <select
                    value={(paramValues.later_column as string) ?? ""}
                    onChange={(e) => handleParamChange("later_column", e.target.value)}
                    className="h-10 w-full rounded border border-[var(--color-border)] px-3 text-sm outline-none focus:border-[var(--color-action-primary)]"
                  >
                    <option value="">Select column</option>
                    {dateColumnOptions.map((opt) => (
                      <option key={opt.fieldId} value={opt.name}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Generic parameter form — hidden when the rule has its own custom UI above */}
            {!isInReferenceList &&
              !isNotInFuture &&
              !isNotInPast &&
              !isMustBeInPast &&
              !isBeforeOtherDate && (
                <ParameterFields
                  schema={preset.parametersSchema}
                  existingParams={existingParams}
                  values={paramValues}
                  columnName={column?.name ?? ""}
                  onChange={handleParamChange}
                  referenceListOptions={referenceListOptions}
                  columnOptions={columnOptions}
                  allColumnOptions={allColumnOptions}
                  numericColumnOptions={numericColumnOptions}
                  presetCategory={preset.category}
                  requiredOverride={requiredParamKeys}
                  lockedParamKey={primaryParamKey}
                />
              )}

            {!minMaxValid && (
              <p className="text-xs text-red-500">Min must be less than or equal to max.</p>
            )}
          </div>

          {/* ── Right column: rule name, criticality, description ─ */}
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold">
                Rule name
                <span className="ml-1 font-normal text-[var(--color-text-tertiary)]">
                  (auto-generated, click to edit)
                </span>
              </label>
              <input
                value={ruleKey}
                onChange={(event) => setRuleKey(event.target.value)}
                className={`h-10 w-full rounded border px-3 text-sm outline-none focus:border-[var(--color-action-primary)] ${
                  isDuplicateName ? "border-red-500" : "border-[var(--color-border)]"
                }`}
              />
              {isDuplicateName && (
                <p className="mt-1 text-xs text-red-500">
                  A rule named "{ruleKey.trim()}" already exists on this schema. Choose a different
                  name.
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold">
                User-facing description
                <span className="ml-1 font-normal text-[var(--color-text-tertiary)]">
                  (optional)
                </span>
              </label>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="h-24 w-full resize-none rounded border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-action-primary)]"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex shrink-0 justify-end gap-3">
          {!isEditing && (
            <button
              type="button"
              onClick={onBack}
              className="rounded border border-[var(--color-border)] px-5 py-2 text-sm"
            >
              Back
            </button>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="rounded bg-[var(--color-action-primary)] px-5 py-2 text-sm font-semibold text-white hover:bg-[var(--color-action-primary-hover)] disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
          >
            Save Rule
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Column multi-select dropdown with checkboxes ─────────────

function ColumnMultiSelect({
  label,
  isReq,
  selectedCols,
  columnOptions,
  onChange,
}: {
  label: string;
  isReq: boolean;
  selectedCols: string[];
  columnOptions: { name: string; fieldId: string }[];
  onChange: (next: string[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const orphanedCols = selectedCols.filter(
    (name) => !columnOptions.some((opt) => opt.name === name)
  );

  return (
    <div className="relative">
      <label className="mb-1 block text-xs font-semibold">
        {label}
        {isReq && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={`h-10 w-full rounded border px-3 text-left text-sm outline-none focus:border-[var(--color-action-primary)] ${
          orphanedCols.length > 0 ? "border-red-400" : "border-[var(--color-border)]"
        }`}
      >
        {selectedCols.length === 0 ? (
          <span className="text-[var(--color-text-tertiary)]">Select columns…</span>
        ) : (
          selectedCols.join(", ")
        )}
      </button>
      {orphanedCols.length > 0 && (
        <p className="mt-1 text-xs text-red-500">
          {orphanedCols.length === 1
            ? `Column "${orphanedCols[0]}" no longer exists. Remove it to resolve the conflict.`
            : `Columns ${orphanedCols.map((c) => `"${c}"`).join(", ")} no longer exist. Remove them to resolve the conflict.`}
        </p>
      )}
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded border border-[var(--color-border)] bg-white shadow-lg">
          <div className="max-h-44 overflow-y-auto p-1.5 space-y-0.5">
            {orphanedCols.map((name) => (
              <label
                key={`orphan-${name}`}
                className="flex cursor-pointer items-center gap-2 rounded bg-red-50 px-2 py-1 text-xs"
              >
                <input
                  type="checkbox"
                  checked
                  onChange={() => onChange(selectedCols.filter((n) => n !== name))}
                  className="h-3.5 w-3.5 accent-red-500"
                />
                <span className="text-red-600">{name}</span>
                <span className="ml-auto text-red-400">(deleted)</span>
              </label>
            ))}
            {columnOptions.map((opt) => {
              const optKey = opt.name.trim().toLowerCase();
              return (
                <label
                  key={opt.fieldId}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-[var(--color-bg-secondary)]"
                >
                  <input
                    type="checkbox"
                    checked={selectedCols.some((n) => n.trim().toLowerCase() === optKey)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...selectedCols, opt.name]
                        : selectedCols.filter((n) => n.trim().toLowerCase() !== optKey);
                      onChange(next);
                    }}
                    className="h-3.5 w-3.5 accent-[var(--color-action-primary)]"
                  />
                  {opt.name}
                </label>
              );
            })}
          </div>
          <div className="border-t border-[var(--color-border)] p-2">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full rounded bg-[var(--color-action-primary)] py-1.5 text-xs font-semibold text-white hover:bg-[var(--color-action-primary-hover)]"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Generic parameter form ────────────────────────────────────

const LABEL_OVERRIDES: Record<string, string> = {
  tolerance: "Within a tolerance of",
  total_column: "Values in",
  x_column: "x",
  components: "Equal the sum of values in",
  z_column: "z",
  y_column: "y",
  earlier_column: "Earlier date",
  later_column: "Later date",
};

function ParameterFields({
  schema,
  existingParams,
  values,
  columnName,
  onChange,
  referenceListOptions = [],
  columnOptions = [],
  allColumnOptions = [],
  numericColumnOptions = [],
  requiredOverride,
  presetCategory,
  lockedParamKey,
}: {
  schema?: Record<string, unknown>;
  existingParams: Record<string, unknown>;
  values: Record<string, unknown>;
  columnName: string;
  onChange: (key: string, value: unknown) => void;
  referenceListOptions?: { id: string; name: string }[];
  columnOptions?: { name: string; fieldId: string }[];
  allColumnOptions?: { name: string; fieldId: string }[];
  numericColumnOptions?: { name: string; fieldId: string }[];
  requiredOverride?: string[];
  presetCategory?: string;
  lockedParamKey?: string;
}) {
  const properties =
    (schema?.properties as Record<string, Record<string, unknown>> | undefined) ?? {};
  const required = requiredOverride ?? (schema?.required as string[]) ?? [];
  const propertyEntries = Object.entries(properties);

  if (propertyEntries.length === 0 && Object.keys(existingParams).length === 0) {
    return (
      <div>
        <label className="mb-1 block text-xs font-semibold">Values in</label>
        <input
          value={columnName}
          readOnly
          className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm"
        />
      </div>
    );
  }

  // No schema but have existing params (edit case with inline-constructed preset)
  if (propertyEntries.length === 0) {
    return (
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-semibold">Values in</label>
          <input
            value={columnName}
            readOnly
            className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm"
          />
        </div>
        {Object.entries(existingParams).map(([key, val]) => (
          <ExistingParamField
            key={key}
            paramKey={key}
            value={values[key] ?? val}
            onChange={onChange}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* no anchor column -- nothing informational to show here */}
      {!lockedParamKey && columnName && (
        <div>
          <label className="mb-1 block text-xs font-semibold">Values in</label>
          <input
            value={columnName}
            readOnly
            className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm"
          />
        </div>
      )}
      {propertyEntries.map(([key, prop]) => {
        const type = prop.type as string;
        const isReq = required.includes(key);
        const label = LABEL_OVERRIDES[key] ?? key.replace(/_/g, " ");
        const val = values[key];

        if (key === lockedParamKey) {
          return (
            <div key={key}>
              <label className="mb-1 block text-xs font-semibold">{label}</label>
              <input
                value={(val as string) ?? ""}
                readOnly
                className="h-10 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 text-sm"
              />
            </div>
          );
        }

        if (
          key.endsWith("_column") &&
          (columnOptions.length > 0 || numericColumnOptions.length > 0)
        ) {
          const isIfThen = presetCategory === "If-Then";
          const isArithmetic = presetCategory === "Arithmetic";
          // If-Then: x must be Integer/Currency; y can be any column.
          // Arithmetic: all _column params accept any numeric column.
          // All other rules: same-type column options.
          const opts =
            key === "x_column" && isIfThen
              ? numericColumnOptions.length > 0
                ? numericColumnOptions
                : columnOptions
              : key === "y_column" && isIfThen
                ? allColumnOptions.length > 0
                  ? allColumnOptions
                  : columnOptions
                : isArithmetic && numericColumnOptions.length > 0
                  ? numericColumnOptions
                  : columnOptions;
          const colVal = (val as string) ?? "";
          const isOrphaned = colVal.length > 0 && !opts.some((opt) => opt.name === colVal);
          return (
            <div key={key}>
              <label className="mb-1 block text-xs font-semibold">
                {label}
                {isReq && <span className="ml-0.5 text-red-500">*</span>}
              </label>
              <select
                value={isOrphaned ? "" : colVal}
                onChange={(e) => onChange(key, e.target.value)}
                className={`h-10 w-full rounded border px-3 text-sm outline-none focus:border-[var(--color-action-primary)] ${
                  isOrphaned ? "border-red-400" : "border-[var(--color-border)]"
                }`}
              >
                <option value="">Select column</option>
                {opts.map((opt) => (
                  <option key={opt.fieldId} value={opt.name}>
                    {opt.name}
                  </option>
                ))}
              </select>
              {isOrphaned && (
                <p className="mt-1 text-xs text-red-500">
                  Column &quot;{colVal}&quot; no longer exists. Select a replacement to resolve the
                  conflict.
                </p>
              )}
            </div>
          );
        }

        if (type === "integer" || type === "number") {
          if (key === "tolerance") {
            return (
              <div key={key} className="flex items-center gap-2">
                <label className="text-xs font-semibold whitespace-nowrap">{label}</label>
                <input
                  type="number"
                  value={(val as number) ?? ""}
                  min={0}
                  step={0.01}
                  onChange={(e) => onChange(key, Number(e.target.value))}
                  className="h-8 w-20 rounded border border-[var(--color-border)] px-2 text-sm outline-none focus:border-[var(--color-action-primary)]"
                />
              </div>
            );
          }
          return (
            <div key={key}>
              <label className="mb-1 block text-xs font-semibold">
                {label}
                {isReq && <span className="ml-0.5 text-red-500">*</span>}
              </label>
              <input
                type="number"
                value={(val as number) ?? ""}
                min={(prop.minimum as number) ?? undefined}
                max={(prop.maximum as number) ?? undefined}
                onChange={(e) => onChange(key, Number(e.target.value))}
                className="h-10 w-full rounded border border-[var(--color-border)] px-3 text-sm outline-none focus:border-[var(--color-action-primary)]"
              />
            </div>
          );
        }

        if (type === "boolean") {
          return (
            <label key={key} className="flex cursor-pointer items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={(val as boolean) ?? false}
                onChange={(e) => onChange(key, e.target.checked)}
                className="h-3.5 w-3.5 accent-[var(--color-action-primary)]"
              />
              <span className="font-semibold">{label}</span>
            </label>
          );
        }

        if (type === "array" && (columnOptions.length > 0 || numericColumnOptions.length > 0)) {
          const opts =
            presetCategory === "Arithmetic" && numericColumnOptions.length > 0
              ? numericColumnOptions
              : columnOptions;
          return (
            <ColumnMultiSelect
              key={key}
              label={label}
              isReq={isReq}
              selectedCols={(val as string[]) ?? []}
              columnOptions={opts}
              onChange={(next) => onChange(key, next)}
            />
          );
        }

        if (type === "array") {
          const arrayVal = (val as string[]) ?? [];
          return (
            <div key={key}>
              <label className="mb-1 block text-xs font-semibold">
                {label}
                {isReq && <span className="ml-0.5 text-red-500">*</span>}
                <span className="ml-1 font-normal text-[var(--color-text-tertiary)]">
                  (comma-separated)
                </span>
              </label>
              <textarea
                value={arrayVal.join(", ")}
                onChange={(e) =>
                  onChange(
                    key,
                    e.target.value
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean)
                  )
                }
                className="h-20 w-full resize-none rounded border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-action-primary)]"
              />
            </div>
          );
        }

        const xRef = prop["x-ref"] as string | undefined;
        if (xRef === "reference_list") {
          return (
            <div key={key}>
              <label className="mb-1 block text-xs font-semibold">
                {label}
                {isReq && <span className="ml-0.5 text-red-500">*</span>}
              </label>
              <select
                value={(val as string) ?? ""}
                onChange={(e) => onChange(key, e.target.value)}
                className="h-10 w-full rounded border border-[var(--color-border)] px-3 text-sm outline-none focus:border-[var(--color-action-primary)]"
              >
                <option value="">Select a reference list</option>
                {referenceListOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        return (
          <div key={key}>
            <label className="mb-1 block text-xs font-semibold">
              {label}
              {isReq && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={(val as string) ?? ""}
              onChange={(e) => onChange(key, e.target.value)}
              className="h-10 w-full rounded border border-[var(--color-border)] px-3 text-sm outline-none focus:border-[var(--color-action-primary)]"
            />
          </div>
        );
      })}
    </div>
  );
}

function ExistingParamField({
  paramKey,
  value,
  onChange,
}: {
  paramKey: string;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
}) {
  const label = paramKey.replace(/_/g, " ");

  if (typeof value === "boolean") {
    return (
      <label className="flex cursor-pointer items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(paramKey, e.target.checked)}
          className="h-3.5 w-3.5 accent-[var(--color-action-primary)]"
        />
        <span className="font-semibold">{label}</span>
      </label>
    );
  }

  if (typeof value === "number") {
    return (
      <div>
        <label className="mb-1 block text-xs font-semibold">{label}</label>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(paramKey, Number(e.target.value))}
          className="h-10 w-full rounded border border-[var(--color-border)] px-3 text-sm outline-none focus:border-[var(--color-action-primary)]"
        />
      </div>
    );
  }

  if (Array.isArray(value)) {
    return (
      <div>
        <label className="mb-1 block text-xs font-semibold">
          {label}
          <span className="ml-1 font-normal text-[var(--color-text-tertiary)]">
            (comma-separated)
          </span>
        </label>
        <textarea
          value={(value as string[]).join(", ")}
          onChange={(e) =>
            onChange(
              paramKey,
              e.target.value
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean)
            )
          }
          className="h-20 w-full resize-none rounded border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-action-primary)]"
        />
      </div>
    );
  }

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold">{label}</label>
      <input
        type="text"
        value={String(value ?? "")}
        onChange={(e) => onChange(paramKey, e.target.value)}
        className="h-10 w-full rounded border border-[var(--color-border)] px-3 text-sm outline-none focus:border-[var(--color-action-primary)]"
      />
    </div>
  );
}

function generateRuleKey(columnName: string, presetTitle: string) {
  return [columnName, presetTitle]
    .filter(Boolean)
    .join("_")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_")
    .replace(/>/g, "greater_than")
    .replace(/</g, "less_than")
    .replace(/=/g, "equals")
    .replace(/[^a-z0-9_]/g, "");
}
